import React, { useCallback, useEffect, useState } from "react";
import { Laptop, Trash2, ShieldCheck, Pencil, Check, X, MapPin } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import {
  getDeviceId,
  getRememberedFamily,
  forgetFamily,
} from "@/lib/familyDevice";

// Card shown on the Family Portal listing every browser that ran
// "Remember this device" for this household. Users can revoke any
// entry; if this current browser gets revoked from elsewhere the
// heartbeat below will notice and force a fresh sign-in next time.
export default function TrustedDevicesCard({ token }) {
  const [devices, setDevices] = useState(null); // null = loading, [] = empty
  const [editingId, setEditingId] = useState(null);
  const [draftLabel, setDraftLabel] = useState("");
  const currentId = getDeviceId();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/families/token/${token}/devices`);
      setDevices(data.devices || []);
    } catch (e) {
      setDevices([]);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Heartbeat + auto-forget if this device has been revoked from another
  // browser. Only runs when the client already claims this browser is
  // trusted for this token.
  useEffect(() => {
    if (getRememberedFamily() !== token) return;
    let cancelled = false;
    api
      .post(`/families/token/${token}/devices/${currentId}/heartbeat`)
      .then((r) => {
        if (cancelled) return;
        if (r.data?.trusted === false) {
          forgetFamily();
          toast.warning("This device was signed out from another browser.");
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, currentId]);

  const revoke = async (deviceId) => {
    try {
      await api.delete(`/families/token/${token}/devices/${deviceId}`);
      if (deviceId === currentId) {
        forgetFamily();
        toast.success("This device was revoked. You'll need to sign in again.");
      } else {
        toast.success("Device revoked.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to revoke");
    }
  };

  const startRename = (dev) => {
    setEditingId(dev.device_id);
    setDraftLabel(dev.label || "");
  };

  const cancelRename = () => {
    setEditingId(null);
    setDraftLabel("");
  };

  const saveRename = async (deviceId) => {
    const label = draftLabel.trim();
    if (!label) {
      toast.error("Give the device a name.");
      return;
    }
    try {
      const { data } = await api.patch(
        `/families/token/${token}/devices/${deviceId}`,
        { label }
      );
      setDevices(data.devices || []);
      setEditingId(null);
      setDraftLabel("");
      toast.success("Device renamed.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rename failed");
    }
  };

  if (devices === null) return null;
  if (devices.length === 0) return null;

  return (
    <div
      className="bg-white border border-[#e6e2d6] rounded-2xl p-5 mb-6"
      data-testid="portal-trusted-devices-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4 w-4 text-[#1a2947]" />
        <div className="label-eyebrow text-[#1a2947]">Trusted devices</div>
      </div>
      <p className="text-xs text-stone-500 mb-4">
        Browsers where "Remember this device" is on. Revoke any you don't
        recognize.
      </p>
      <ul className="divide-y divide-[#f0ebde]">
        {devices.map((d) => {
          const isThis = d.device_id === currentId;
          const isEditing = editingId === d.device_id;
          return (
            <li
              key={d.device_id}
              data-testid={`trusted-device-${d.device_id}`}
              className="flex items-start gap-3 py-3"
            >
              <Laptop className="h-4 w-4 mt-1 text-stone-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {isEditing ? (
                    <input
                      autoFocus
                      value={draftLabel}
                      onChange={(e) => setDraftLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename(d.device_id);
                        if (e.key === "Escape") cancelRename();
                      }}
                      maxLength={60}
                      className="text-sm font-medium text-stone-900 border-b border-stone-300 focus:border-[#1a2947] outline-none bg-transparent min-w-0 flex-1"
                      data-testid={`rename-input-${d.device_id}`}
                    />
                  ) : (
                    <>
                      <span className="text-sm font-medium text-stone-900">
                        {d.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => startRename(d)}
                        title="Rename this device"
                        data-testid={`rename-device-${d.device_id}`}
                        className="text-stone-400 hover:text-[#1a2947] transition-colors"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </>
                  )}
                  {isThis && (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-semibold px-2 py-0.5">
                      This device
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-stone-500 mt-0.5 tabular-nums">
                  {d.remembered_at && (
                    <>
                      Trusted{" "}
                      {new Date(d.remembered_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </>
                  )}
                  {d.last_seen_at && (
                    <>
                      {" · Last seen "}
                      {new Date(d.last_seen_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </>
                  )}
                </div>
                {d.last_location && (
                  <div
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-stone-500"
                    data-testid={`device-location-${d.device_id}`}
                  >
                    <MapPin className="h-3 w-3 text-stone-400" />
                    <span>Last near {d.last_location}</span>
                  </div>
                )}
              </div>
              {isEditing ? (
                <div className="shrink-0 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => saveRename(d.device_id)}
                    data-testid={`rename-save-${d.device_id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={cancelRename}
                    data-testid={`rename-cancel-${d.device_id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-stone-700"
                  >
                    <X className="h-3.5 w-3.5" />
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => revoke(d.device_id)}
                  data-testid={`revoke-device-${d.device_id}`}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 hover:underline"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Revoke
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
